<template>
  <div class="pay-root">
  <div class="cashier np-page" v-if="[0, 1].includes(Number(vm.orderInfo?.status))">
    <van-skeleton title :row="18" :loading="pageLoading">
      <div class="cashier-hero">
        <div class="hero-label">Payment Amount</div>
        <div class="hero-amount">
          <span>₹{{ payableAmount }}</span>
          <template v-if="hasDiscount">
            <span class="hero-divider">|</span>
            <span class="hero-original">₹{{ originalAmount }}</span>
          </template>
        </div>
        <div class="hero-timer">{{ formattedCountdown }}</div>
      </div>

      <main class="cashier-body">
        <div class="pay-tabs">
          <button
            type="button"
            :class="['pay-tab', activeTab === 'direct' ? 'active' : '']"
            @click="activeTab = 'direct'"
          >
            Direct Transfer
          </button>
          <button
            type="button"
            :class="['pay-tab', activeTab === 'scan' ? 'active' : '']"
            @click="activeTab = 'scan'"
          >
            Scan QRCode
          </button>
        </div>

        <section class="method-card">
          <div class="method-title">Select Payment Method</div>
          <div class="method-grid">
            <button
              v-for="(item, index) in visiblePayMethods"
              :key="item.name"
              type="button"
              class="method-item"
              @click="onSelectPayMethod(item, index)"
            >
              <img v-if="item.name !== 'PhonePe'" class="method-img" :src="item.icon" alt="" />
              <span v-else class="method-logo phonepe">पे</span>
              <span class="method-name">{{ methodDisplayName(item.name) }}</span>
            </button>
          </div>
        </section>

        <div class="warning-bar">
          <span class="warning-icon">!</span>
          <span>{{ warningText }}</span>
        </div>

        <section v-if="activeTab === 'scan'" class="scan-panel">
          <div class="scan-amount">₹{{ payableAmount }}</div>
          <div class="scan-subtitle">Use Mobile Scan code to pay</div>
          <div class="scan-frame">
            <img v-if="vm.qrImgUrl" class="scan-qr" :src="vm.qrImgUrl" alt="qr" />
          </div>
        </section>

        <section v-else class="direct-panel">
          <div class="direct-title">1. Transfer {{ payableAmount }} RS to the following upi</div>
          <div class="direct-copy-row">
            <span>{{ vm.orderInfo.upi || "-" }}</span>
            <button type="button" class="copy-icon-btn" @click="copyText(vm.orderInfo.upi)">⧉</button>
          </div>
          <div class="direct-copy-row">
            <span>{{ payableAmount }} RS</span>
            <button type="button" class="copy-icon-btn" @click="copyText(payableAmount)">⧉</button>
          </div>
        </section>

        <section class="utr-panel">
          <div class="utr-title">After payment, submit 12-digit UTR here</div>
          <Utypayment :embedded="true" />
        </section>

        <div class="np-upi-bhim-logo">
          <img src="@/assets/img/pay/logo_upi_bhim.png" alt="UPI BHIM" />
        </div>
      </main>
    </van-skeleton>

    <!-- 接口 loading -->
    <div class="overlay" v-if="apiLoading">
      <div class="overlay-box">
        <van-loading size="28px" vertical type="spinner"
          >Loading...</van-loading
        >
        <div class="overlay-text">
          Thoda wait karo, order generate ho raha hai. Please don’t leave, bas
          hone hi wala hai!
        </div>
      </div>
    </div>

    <van-action-sheet :show="vm.showPopup" @update:show="vm.showPopup = $event" @close="onCloseSheet">
      <div class="np-sheet-content cashier-sheet">
        <div class="sheet-method-title">{{ methodDisplayName(vm.selectedPayMethod) }}</div>
        <div v-html="vm.sheetText" class="np-sheet-text"></div>

        <!-- ⭐ PhonePe 大额：展示专用二维码 -->
        <div v-if="showPhonePeQr" class="np-phonepe-qr">
          <img :src="vm.phonePeQrImgUrl" class="np-qr-img" alt="PhonePe QR" />
          <button type="button" class="np-btn-outline" @click="downloadPhonePeQr">Save QRCode</button>
        </div>

        <div class="np-sheet-foot">
          <div class="np-card-subtitle" style="margin-top: 0">{{ vm.sheetTitle || "Submit 12-digit UTR now" }}</div>
          <Utypayment ref="utrRef" :showArrow="1" :showText="2" :embedded="true" />
        </div>
      </div>
    </van-action-sheet>
  </div>

  <div class="np-status-page" v-if="vm.orderInfo?.status == 2">
    <div class="np-status-card">
      <div class="np-status-icon is-error">
        <img src="@/assets/img/pay/error.png" alt="expired" />
      </div>

      <h1 class="np-status-title">Order expired</h1>
      <p class="np-status-desc">Order not found or expired. Please try again.</p>
      <div class="np-detail-list">
        <div class="np-detail-row"><span class="k">Order ID</span><span class="v">{{ vm.orderInfo?.id || "-" }}</span></div>
        <div class="np-detail-row"><span class="k">Amount</span><span class="v">₹{{ payableAmount }}</span></div>
        <div class="np-detail-row"><span class="k">Status</span><span class="v danger">Expired</span></div>
        <div class="np-detail-row"><span class="k">Create Time</span><span class="v">{{ vm.orderInfo?.createTime || "-" }}</span></div>
      </div>

      <!-- 保留：过期也允许提交 UTR -->
      <div style="margin-top: 16px"><Utypayment :embedded="true" /></div>

      <div class="np-meta-chip">
        <span class="dot"></span>
        <span>Secure payment</span>
      </div>
      <div class="np-upi-bhim-logo">
        <img src="@/assets/img/pay/logo_upi_bhim.png" alt="UPI BHIM" />
      </div>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import QRCode from "qrcode";
import { Toast } from "vant";
import Axios from "@/axios";
import { decrypt } from "@/common/js/utils.js";
import Utypayment from "./utrpayment.vue";

const route = useRoute();
const router = useRouter();
const isPreviewMode =
  process.env.NODE_ENV === "development" || route.query.preview === "1";

const pageLoading = ref(false); // skeleton
const apiLoading = ref(false); // overlay
const countdownSeconds = ref(null); // seconds left
const utrRef = ref(null);
const activeTab = ref("direct");

let countdownTimer = null;
let payLooping = false; // ✅ 防止 /safe/pay status=3 并发 loop

const vm = reactive({
  id: route.query.id,
  orderInfo: { status: 0 },
  cashierLinks: {},

  qrImgUrl: "", // 你已有：通用 UPI QR
  phonePeQrImgUrl: "", // ⭐ 新增：PhonePe 专用 QR
  selectedPayMethod: undefined,

  // sheet
  showPopup: false,
  sheetIcon: "",
  sheetText: "",
  sheetTitle: "",

  scrollList: ["Dear Member , 100% Secure Payment Powered"],

  payMethods: [
    { name: "paytm", icon: require("@/assets/img/pay/s1.png"), isShow: true },
    { name: "PhonePe", icon: require("@/assets/img/pay/s2.png"), isShow: true },
    { name: "upi", icon: require("@/assets/img/pay/s4.png"), isShow: true },
  ],
});

/**
 * API 成功判斷
 * - 目前後端已確認：code=200 才代表成功
 * - 若後端未來再次調整成功碼，需同步修改此處與 Cursor 規則文檔
 */
function isApiSuccess(res) {
  return Number(res?.code) === 200;
}

function debugApiLog(label, payload) {
  console.log(`[pay-debug] ${label}`, payload);
}

/**
 * 訂單資料解析契約（供後續接口變更快速定位）
 * 1) 優先取密文字段: res.result ?? res.data
 * 2) 使用 route/query 的 id 作為 decrypt key
 * 3) decrypt 後必須是可 JSON.parse 的字串
 * 4) status 需轉成 Number，避免 "1"/1 比較錯判
 *
 * 當接口變更時，請優先檢查：
 * - code 是否仍為 200
 * - 加密字段是否仍為 result/data
 * - decrypt key 是否仍為 id
 * - status 字段是否改名/型別改動
 */
function parseOrderPayload(res) {
  const encryptedText = res?.result ?? res?.data;
  debugApiLog("parse source", {
    code: res?.code,
    hasResult: Boolean(res?.result),
    hasData: Boolean(res?.data),
    encryptedLength: encryptedText?.length || 0,
    id: vm.id,
  });
  if (!encryptedText || !vm.id) return null;
  try {
    const decryptedText = decrypt(encryptedText, vm.id);
    debugApiLog("decrypted text", decryptedText);
    const parsed = JSON.parse(decryptedText || "{}");
    parsed.status = Number(parsed?.status);
    debugApiLog("parsed order payload", parsed);
    return parsed;
  } catch (e) {
    console.error("[pay-debug] parseOrderPayload failed", e, {
      res,
      id: vm.id,
      encryptedText,
    });
    return null;
  }
}

function hasAmountValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== "" && !Number.isNaN(Number(value));
}

function formatAmountValue(value) {
  if (!hasAmountValue(value)) return "";
  return String(value).trim();
}

/** ✅ 计算：应付金额 */
const originalAmount = computed(() => formatAmountValue(vm.orderInfo?.amount));
const preferentialPayAmount = computed(() => {
  const preferentialAmount = vm.orderInfo?.preferentialAmount;
  return hasAmountValue(preferentialAmount) && Number(preferentialAmount) > 0
    ? formatAmountValue(preferentialAmount)
    : "";
});
const payableAmount = computed(() => {
  return preferentialPayAmount.value || originalAmount.value || "-";
});
const hasDiscount = computed(() => {
  if (!preferentialPayAmount.value || !originalAmount.value) return false;
  return Number(preferentialPayAmount.value) !== Number(originalAmount.value);
});
const visiblePayMethods = computed(() =>
  vm.payMethods.filter((item) => item.isShow && item.name !== "upi")
);
const warningText = computed(() =>
  activeTab.value === "scan"
    ? "Do not use the same QR code to pay multiple times. Multiple payments are not valid!!!"
    : "Payment can only be made once. Multiple payments are not valid!!!"
);

function methodDisplayName(name) {
  const normalized = String(name || "").toLowerCase();
  if (normalized === "phonepe") return "PhonePe";
  if (normalized === "paytm") return "Paytm";
  if (normalized === "gpay") return "GPay";
  if (normalized === "upi") return "UPI";
  return name || "";
}

/** ✅ 倒计时显示 */
const formattedCountdown = computed(() => {
  if (countdownSeconds.value === null || countdownSeconds.value === undefined)
    return "00:00";
  const m = Math.floor(Math.max(0, countdownSeconds.value) / 60);
  const s = Math.max(0, countdownSeconds.value) % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
});

/** ✅ 启动倒计时：每秒 -1；每 10 秒 query；归零再 query 一次 */
function startCountdown() {
  if (countdownTimer) clearInterval(countdownTimer);

  countdownTimer = setInterval(() => {
    if (countdownSeconds.value > 0) {
      countdownSeconds.value--;

      if (countdownSeconds.value % 10 === 0) {
        queryOrderStatus();
      }
    } else {
      clearInterval(countdownTimer);
      countdownTimer = null;
      queryOrderStatus();
    }
  }, 1000);
}

/** ✅ 关闭 sheet */
function onCloseSheet() {
  vm.sheetText = "";
  vm.sheetTitle = "";
  vm.selectedPayMethod = undefined; // ⭐ 必加
  utrRef.value?.closeModal?.();
}

/** ✅ 复制：clipboard 失败自动 fallback，不污染控制台 */
async function copyText(text) {
  if (!text) return Toast({ message: "Nothing to copy", duration: 1500 });
  const value = String(text);

  try {
    await navigator.clipboard.writeText(value);
    Toast({ message: "Copy Successful", duration: 1500 });
  } catch (e) {
    const ta = document.createElement("textarea");
    ta.value = value;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    Toast({ message: "Copy Successful", duration: 1500 });
  }
}

/** ✅ 下载二维码 */
function downloadQrCode() {
  if (!vm.qrImgUrl) return;
  const link = document.createElement("a");
  link.href = vm.qrImgUrl;
  link.download = "QRCode.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/** ✅ 选择支付方式：直接使用后端返回的 cashier 深链唤醒 */
function onSelectPayMethod(item, index) {
  vm.selectedPayMethod = item.name;
  openAppDeepLink(item.name);
}

/** ✅ 唤起 app deep link（保持你原逻辑） */
function openAppDeepLink(name) {
  const n = String(name || "").toLowerCase();
  if (n === "paytm") window.location.href = vm.cashierLinks?.paytm;
  else if (n === "phonepe") window.location.href = vm.cashierLinks?.phonepe;
  else if (n === "upi") window.location.href = vm.cashierLinks?.upi;
  else if (n === "gpay") window.location.href = vm.cashierLinks?.gpay;
}

/** ✅ 生成二维码（只生成你页面用到的一张） */
function buildQrCode() {
  // 通用 UPI
  if (vm.cashierLinks?.upi) {
    QRCode.toDataURL(
      vm.cashierLinks.upi,
      { errorCorrectionLevel: "L", margin: 2, width: 180 },
      (_, url) => (vm.qrImgUrl = url)
    );
  }

  // ⭐ PhonePe 专用 QR
  if (vm.cashierLinks?.upi) {
    QRCode.toDataURL(
      vm.cashierLinks.upi,
      { errorCorrectionLevel: "L", margin: 2, width: 180 },
      (_, url) => (vm.phonePeQrImgUrl = url)
    );
  }
}

const showPhonePeQr = computed(() => {
  return false;
});

/** ✅ 初始化：/safe/pay（status==3 则 loop，并发加锁） */
async function initOrder(type) {
  if (type === "loop" && payLooping) return;
  if (type === "loop") payLooping = true;

  try {
    apiLoading.value = true;
    const res = await Axios("post", "/safe/pay", { id: vm.id });
    debugApiLog("/safe/pay raw response", res);

    if (!isApiSuccess(res)) {
      debugApiLog("/safe/pay rejected by code", res);
      apiLoading.value = false;
      payLooping = false;
      return;
    }

    const result = parseOrderPayload(res);
    if (!result) {
      debugApiLog("/safe/pay parsed result is null", res);
      apiLoading.value = false;
      payLooping = false;
      return;
    }
    debugApiLog("/safe/pay final result", result);

    // status==3：保持 loading，10 秒后继续 pay
    if (result.status == 3) {
      setTimeout(() => initOrder("loop"), 10000);
      return;
    }

    // 结束 loading
    apiLoading.value = false;
    payLooping = false;

    // 落地数据
    vm.orderInfo = result;
    vm.cashierLinks = result.cashier || {};

    // 倒计时
    countdownSeconds.value = Math.max(0, (result.expiredMinute || 0) * 60);
    startCountdown();

    // 二维码
    buildQrCode();

    // 支付方式显示控制
    const phonePe = vm.payMethods.find((x) => x.name === "PhonePe");
    if (phonePe)
      phonePe.isShow = vm.cashierLinks?.phonepe === "null" ? false : true;

    const paytm = vm.payMethods.find((x) => x.name === "paytm");
    if (paytm) paytm.isShow = vm.cashierLinks?.paytm === "null" ? false : true;

    // 滚动提示
    if (result.showText) vm.scrollList = [...vm.scrollList, result.showText];

    // 直接成功：跳成功页
    if (result.status == 1 && !isPreviewMode) {
      sessionStorage.setItem("checkOutData", JSON.stringify(result));
      router.push(`/checkout?id=${vm.id}&type=success`);
    }
  } catch (e) {
    console.error("[pay-debug] /safe/pay request failed", e);
    apiLoading.value = false;
    payLooping = false;
  }
}

/** ✅ 查询状态：只处理 1/2；3 回流 pay loop（避免重复触发） */
async function queryOrderStatus() {
  try {
    const res = await Axios("post", "/safe/query", { id: vm.id });
    debugApiLog("/safe/query raw response", res);
    if (!isApiSuccess(res)) {
      debugApiLog("/safe/query rejected by code", res);
      return;
    }

    const result = parseOrderPayload(res);
    if (!result) {
      debugApiLog("/safe/query parsed result is null", res);
      return;
    }
    debugApiLog("/safe/query final result", result);
    const st = Number(result.status); // ✅ 改这一行

    if (st === 1 && !isPreviewMode) {
      sessionStorage.setItem("checkOutData", JSON.stringify(result));
      router.push(`/checkout?id=${vm.id}&type=success`);
      return;
    }

    if (st === 2) {
      vm.orderInfo = { ...vm.orderInfo, ...result, status: 2 };
      return;
    }

    if (st === 3) {
      // ✅ 防止并发 loop
      apiLoading.value = true;
      initOrder("loop");
      return;
    }
  } catch (e) {
    console.error("[pay-debug] /safe/query request failed", e);
  }
}

function downloadPhonePeQr() {
  if (!vm.phonePeQrImgUrl) return;
  const a = document.createElement("a");
  a.href = vm.phonePeQrImgUrl;
  a.download = "PhonePe-QRCode.png";
  a.click();
}

onMounted(() => {
  document.title = "deposit";
  window.globalData = "once";
  sessionStorage.setItem("checkOutData", "");

  pageLoading.value = true;
  setTimeout(() => {
    pageLoading.value = false;
    initOrder();
  }, 500);
});

onUnmounted(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer);
    countdownTimer = null;
  }
});
</script>

<style scoped lang="less">
/* ===== 页面主题（Easy 墨绿暖米色） ===== */
.cashier {
  min-height: 100vh;
  width: 100%;
  padding: 10px 0px 56px;
  background: radial-gradient(
      1200px 600px at 50% -220px,
      rgba(31, 111, 93, 0.16),
      transparent 60%
    ),
    var(--easy-color-bg);

  --van-primary-color: var(--easy-color-primary);
  --van-button-primary-background: var(--easy-color-primary);
  --van-button-primary-border-color: var(--easy-color-primary);
}

/* 顶部栏 */
.topbar {
  position: sticky;
  top: 0;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 8px 8px;
  background: rgba(244, 239, 230, 0.82);
  backdrop-filter: blur(10px);
}
.topbar-title {
  font-size: 16px;
  font-weight: 900;
  color: var(--easy-color-text);
}
.topbar-timer {
  font-size: 12px;
  font-weight: 900;
  color: var(--easy-color-chip);
  padding: 6px 10px;
  border-radius: 999px;
  background: var(--easy-color-accent-bg);
}

/* 卡片基础 */
.card {
  background: var(--easy-color-surface);
  border-radius: 22px;
  padding: 14px;
  box-shadow: 0 16px 46px rgba(31, 111, 93, 0.14);
  border: 1px solid var(--easy-color-accent-border);
  backdrop-filter: blur(10px);
  margin-bottom: 10px;
}

.card-title {
  font-size: 14px;
  font-weight: 900;
  color: var(--easy-color-text);
}
.card-subtitle {
  margin-top: 10px;
  font-size: 13px;
  font-weight: 900;
  color: var(--easy-color-text);
}

/* 金额 */
.amount-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
.amount-line .label {
  font-size: 13px;
  font-weight: 800;
  color: var(--easy-color-subtext);
}
.amount-line .value {
  font-size: 24px;
  font-weight: 900;
  color: var(--easy-color-text);
}
.discount {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed var(--easy-color-accent-border);
}
.d-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 800;
}
.strike {
  text-decoration: line-through;
  color: var(--easy-color-chip);
}
.minus {
  color: #ef4444;
}

/* 二维码 */
.qr-box {
  margin-top: 10px;
  display: flex;
  justify-content: center;
}
.qr-img {
  width: 188px;
  height: 188px;
  border-radius: 18px;
  background: #fff;
  padding: 10px;
  box-shadow: 0 10px 30px rgba(31, 111, 93, 0.12);
}
.qr-actions {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}
.ghost-btn {
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid rgba(31, 111, 93, 0.35);
  color: var(--easy-color-primary);
  font-weight: 900;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.75);
}
.divider {
  margin: 12px 0 4px;
  text-align: center;
  font-size: 12px;
  font-weight: 900;
  color: var(--easy-color-chip);
}

/* 支付方式列表 */
.paylist {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.pay-item {
  height: 56px;
  border-radius: 18px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--easy-color-accent-bg);
  border: 1px solid var(--easy-color-accent-border);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.pay-item:active {
  transform: scale(0.992);
  box-shadow: 0 6px 16px rgba(31, 111, 93, 0.12);
}
.pay-item .left {
  width: 110px;
  display: flex;
  align-items: center;
}
.pay-item .left img {
  max-width: 100%;
  height: 24px;
  object-fit: contain;
  mix-blend-mode: multiply;
  background: transparent;
}
.pay-item .right {
  color: var(--easy-color-subtext);
  font-weight: 900;
}

/* 手动转账 */
.manual-card .step {
  margin-top: 10px;
}
.step-title {
  font-size: 13px;
  font-weight: 900;
  color: var(--easy-color-text);
  margin-bottom: 8px;
}
.copy-row {
  display: flex;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid var(--easy-color-accent-border);
  background: var(--easy-color-accent-bg);
}
.copy-text {
  flex: 1;
  padding: 10px 12px;
  font-weight: 900;
  color: var(--easy-color-text);
  font-size: 14px;
  word-break: break-all;
}
.copy-btn {
  width: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  color: #fff;
  background: var(--easy-color-primary);
}
.step-tip {
  margin-top: 6px;
  font-size: 12px;
  color: var(--easy-color-chip);
}

/* 底部 notice */
.bottom-notice {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 15;
  padding: 8px 12px;
  background: rgba(31, 111, 93, 0.92);
  backdrop-filter: blur(10px);
}
:deep(.notice-swipe) {
  height: 28px;
  line-height: 28px;
}
:deep(.van-notice-bar) {
  background: transparent !important;
}

/* overlay */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(2, 6, 23, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
}
.overlay-box {
  width: 86%;
  max-width: 360px;
  background: var(--easy-color-surface);
  border-radius: 22px;
  padding: 18px 16px;
  text-align: center;
  box-shadow: 0 18px 55px rgba(31, 111, 93, 0.18);
  border: 1px solid var(--easy-color-accent-border);
  backdrop-filter: blur(10px);
}
.overlay-text {
  margin-top: 10px;
  font-size: 13px;
  line-height: 18px;
  color: var(--easy-color-subtext);
  font-weight: 800;
}
:deep(.overlay .van-loading__spinner) {
  color: var(--easy-color-primary);
}

/* actionSheet 统一 */
.sheet-purple {
  --van-action-sheet-cancel-padding: 0px;
}
.sheet-content {
  padding: 14px 14px 18px;
}
.sheet-head {
  display: flex;
  justify-content: center;
  padding: 6px 0 10px;
}
.sheet-logo {
  height: 34px;
  object-fit: contain;
  mix-blend-mode: multiply;
  background: transparent;
}
.sheet-text {
  font-size: 13px;
  color: var(--easy-color-subtext);
  font-weight: 800;
  line-height: 18px;
  word-break: break-word;
}
.sheet-foot {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed var(--easy-color-accent-border);
}
.sheet-title {
  font-size: 13px;
  font-weight: 900;
  color: var(--easy-color-text);
  margin-bottom: 8px;
}

/* ===== errorPage：Easy 状态页 ===== */
.expired {
  min-height: 100vh;
  width: 100%;
  padding: 18px 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
      1200px 600px at 50% -220px,
      rgba(31, 111, 93, 0.16),
      transparent 60%
    ),
    var(--easy-color-bg);

  --van-primary-color: var(--easy-color-primary);
}
.expired-card {
  width: 100%;
  max-width: 420px;
  background: var(--easy-color-surface);
  border-radius: 22px;
  padding: 18px 16px;
  box-shadow: 0 18px 55px rgba(31, 111, 93, 0.12);
  border: 1px solid var(--easy-color-accent-border);
  backdrop-filter: blur(10px);
  text-align: center;
}
.expired-icon {
  width: 86px;
  height: 86px;
  margin: 0 auto 10px;
  border-radius: 22px;
  background: var(--easy-color-accent-bg);
  display: flex;
  align-items: center;
  justify-content: center;
}
.expired-icon img {
  width: 54px;
  height: 54px;
  object-fit: contain;
}
.expired-title {
  font-size: 18px;
  font-weight: 900;
  color: var(--easy-color-text);
}
.expired-sub {
  margin-top: 6px;
  font-size: 13px;
  color: var(--easy-color-subtext);
}
.expired-details {
  margin-top: 14px;
  text-align: left;
  padding: 12px;
  border-radius: 16px;
  background: var(--easy-color-accent-bg);
  border: 1px solid var(--easy-color-accent-border);
}
.expired-details .d {
  display: flex;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px dashed var(--easy-color-accent-border);
}
.expired-details .d:last-child {
  border-bottom: 0;
}
.expired-details .k {
  width: 92px;
  font-size: 12px;
  font-weight: 900;
  color: var(--easy-color-subtext);
}
.expired-details .v {
  flex: 1;
  font-size: 12px;
  font-weight: 900;
  color: var(--easy-color-text);
  word-break: break-word;
}
.expired-details .v.danger {
  color: #ef4444;
}
.expired-utr {
  margin-top: 12px;
}
.expired-foot {
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: var(--easy-color-accent-bg);
  color: var(--easy-color-chip);
  font-size: 12px;
  font-weight: 900;
}
.expired-foot .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--easy-color-primary);
  box-shadow: 0 0 0 4px rgba(31, 111, 93, 0.18);
}

/* 兜底撑满 */
:global(html, body, #app) {
  width: 100%;
  max-width: none !important;
  margin: 0 !important;
}

/* ===== PhonePe 大额二维码（ActionSheet 内） ===== */
.phonepe-qr {
  margin-top: 14px;
  padding: 14px 12px;
  border-radius: 20px;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  background: var(--easy-color-accent-bg);
  border: 1px dashed rgba(31, 111, 93, 0.25);
}

/* ActionSheet 里的二维码稍微小一点 */
.phonepe-qr .qr-img {
  width: 168px;
  height: 148px;
  padding: 5px;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(31, 111, 93, 0.12);
}

/* ActionSheet 内的保存按钮复用 ghost 风格 */
.phonepe-qr .ghost-btn {
  margin-top: 2px;
}
</style>

<style scoped lang="less">
.cashier.np-page {
  padding: 0;
  background: #f1ecfb;
  color: #2f263d;
  min-height: 100vh;
  --van-primary-color: #7944d9;
  --van-button-primary-background: linear-gradient(90deg, #8649e6, #7a43dc);
  --van-button-primary-border-color: transparent;
}

.cashier-hero {
  min-height: 218px;
  padding: 20px 16px 24px;
  box-sizing: border-box;
  text-align: center;
  color: #fff;
  background: linear-gradient(180deg, #8649e6 0%, #7a43dc 100%);
}
.hero-label {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}
.hero-amount {
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: clamp(38px, 10vw, 48px);
  font-weight: 500;
  line-height: 1;
}
.hero-original {
  color: rgba(255, 255, 255, 0.62);
  text-decoration: line-through;
}
.hero-divider {
  opacity: 0.82;
}
.hero-timer {
  margin-top: 30px;
  font-size: clamp(24px, 6.5vw, 30px);
  font-weight: 800;
  line-height: 1;
}

.cashier-body {
  padding: 26px 24px 34px;
  background: #f1ecfb;
}
.pay-tabs {
  display: flex;
  align-items: flex-end;
  gap: 26px;
  margin-bottom: 24px;
  overflow: hidden;
}
.pay-tab {
  position: relative;
  flex: 0 0 auto;
  padding: 0 0 12px;
  background: transparent;
  color: #8f8996;
  font-size: clamp(17px, 5vw, 23px);
  font-weight: 800;
  line-height: 1.1;
  white-space: nowrap;
}
.pay-tab.active {
  color: #7944d9;
}
.pay-tab.active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  background: #7944d9;
}

.method-card,
.direct-panel,
.utr-panel {
  border: 1px solid #c7b8e8;
  border-radius: 10px;
  background: #ded4f5;
}
.method-card {
  padding: 16px 18px;
}
.method-title {
  margin-bottom: 16px;
  color: #3d255f;
  font-size: clamp(17px, 4.8vw, 22px);
  font-weight: 800;
}
.method-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
}
.method-item {
  height: 58px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  border: 1px solid #cbbce8;
  border-radius: 10px;
  background: #f1ecfb;
  color: #3d255f;
  overflow: hidden;
}
.method-img {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  object-fit: contain;
  background: #fff;
}
.method-logo.phonepe {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #6b35c9;
  color: #fff;
  font-size: 20px;
  font-weight: 900;
  font-family: Arial, sans-serif;
}
.method-name {
  min-width: 0;
  font-size: clamp(13px, 3.5vw, 16px);
  font-weight: 800;
  white-space: nowrap;
  overflow: visible;
}

.warning-bar {
  margin-top: 14px;
  padding: 10px 18px;
  min-height: 42px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 11px;
  border: 1px solid #b59fe2;
  border-radius: 4px;
  background: #cbb5ef;
  color: #3d2b61;
  font-size: clamp(11px, 3.15vw, 14px);
  font-weight: 500;
  line-height: 1.3;
}
.warning-bar span:last-child {
  flex: 1;
  min-width: 0;
  word-break: normal;
  overflow-wrap: normal;
}
.warning-icon {
  flex: 0 0 18px;
  height: 18px;
  border: 1px solid #3d2b61;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 1;
}

.scan-panel {
  margin-top: 18px;
  text-align: center;
}
.scan-amount {
  color: #2f263d;
  font-size: 31px;
  font-weight: 900;
}
.scan-subtitle {
  margin-top: 8px;
  color: #9a969f;
  font-size: 16px;
  font-weight: 700;
}
.scan-frame {
  position: relative;
  width: 236px;
  height: 236px;
  margin: 14px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.scan-qr {
  width: 196px;
  height: 196px;
  object-fit: contain;
}
.corner {
  position: absolute;
  width: 40px;
  height: 40px;
  border-color: rgba(216, 207, 232, 0.78);
}
.corner.tl { left: 0; top: 0; border-left: 4px solid; border-top: 4px solid; }
.corner.tr { right: 0; top: 0; border-right: 4px solid; border-top: 4px solid; }
.corner.bl { left: 0; bottom: 0; border-left: 4px solid; border-bottom: 4px solid; }
.corner.br { right: 0; bottom: 0; border-right: 4px solid; border-bottom: 4px solid; }

.direct-panel {
  margin-top: 20px;
  padding: 22px 16px 28px;
}
.direct-title {
  color: #2f263d;
  font-size: clamp(12px, 3.25vw, 16px);
  font-weight: 900;
  line-height: 1.25;
  white-space: nowrap;
}
.direct-copy-row {
  margin: 13px auto 0;
  min-height: 52px;
  padding: 0 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  background: #f3effb;
  color: #2f263d;
  font-size: clamp(15px, 4.1vw, 19px);
  font-weight: 900;
  word-break: break-all;
}
.copy-icon-btn {
  flex: 0 0 32px;
  height: 32px;
  background: transparent;
  color: #4a405c;
  font-size: 24px;
}

.utr-panel {
  margin-top: 16px;
  padding: 14px;
}
.utr-title {
  margin-bottom: 9px;
  color: #3d255f;
  font-size: clamp(12px, 3.25vw, 15px);
  font-weight: 800;
  line-height: 1.25;
  white-space: nowrap;
}
.cashier :deep(.np-utr-card),
.utr-panel :deep(.np-utr-card) {
  border-color: transparent;
  border-radius: 16px;
  background: #fff;
  box-shadow: none;
}
.cashier :deep(.np-btn-primary.van-button),
.cashier-sheet :deep(.np-btn-primary.van-button),
.utr-panel :deep(.np-btn-primary.van-button) {
  border-color: transparent;
  border-radius: 999px;
  background: linear-gradient(90deg, #8649e6 0%, #7a43dc 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
}
.cashier :deep(.np-utr-note),
.cashier-sheet :deep(.np-utr-note),
.utr-panel :deep(.np-utr-note) {
  color: #8d8498;
}

.np-upi-bhim-logo {
  margin: 16px auto 0;
  text-align: center;
}
.np-upi-bhim-logo img {
  display: inline-block;
  width: 180px;
  max-width: 54vw;
  max-height: 34px;
  object-fit: contain;
  opacity: 0.5;
}

.cashier-sheet {
  background: #f1ecfb;
}
.sheet-method-title {
  padding: 8px 0 4px;
  text-align: center;
  color: #111;
  font-size: 22px;
  font-weight: 900;
}
.cashier-sheet :deep(.np-sheet-text),
.cashier-sheet :deep(.np-card-subtitle),
.cashier-sheet :deep(.np-utr-field .van-field__label),
.cashier-sheet :deep(.np-utr-field .van-field__control) {
  color: #111;
}
.cashier-sheet :deep(.np-utr-card) {
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}
.cashier-sheet :deep(.np-utr-field) {
  background: #f3effb;
}
.np-phonepe-qr {
  margin-top: 14px;
  padding: 14px;
  border-radius: 12px;
  background: #eee7fb;
  border: 1px dashed rgba(121, 68, 217, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.np-phonepe-qr .np-qr-img {
  width: 168px;
  height: 168px;
}

@media (max-width: 380px) {
  .cashier-body {
    padding-left: 20px;
    padding-right: 20px;
  }
  .pay-tabs {
    gap: 20px;
  }
  .method-card {
    padding: 14px;
  }
  .method-grid {
    gap: 8px;
  }
  .method-item {
    padding: 0 8px;
    gap: 7px;
  }
  .method-name {
    font-size: 13px;
  }
  .direct-title,
  .utr-title {
    font-size: 11px;
  }
}
</style>
